import { useEffect, useRef, useState } from 'react';
import {
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiLoader,
  FiRotateCw,
  FiX,
  FiZoomIn,
  FiZoomOut,
} from 'react-icons/fi';
import { getAttachmentName, isPdfAttachment } from '../../utils/attachments';
import { useAuthenticatedAttachmentUrl } from '../../hooks/useAuthenticatedAttachmentUrl';
import { AppTooltip } from '../ui/AppTooltip';

const ViewerButton = ({ label, children, className = '', ...props }) => (
  <AppTooltip label={label}>
    <button
      type='button'
      className={`flex h-10 w-10 items-center justify-center rounded-[var(--radius-md)] border border-white/15 bg-slate-950/65 text-white shadow-lg backdrop-blur hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-35 ${className}`}
      aria-label={label}
      {...props}
    >
      {children}
    </button>
  </AppTooltip>
);

export const AttachmentViewer = ({ attachments = [], initialIndex = 0, open, onClose, t }) => {
  const [index, setIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const total = attachments.length;
  const attachment = attachments[index];
  const isPdf = isPdfAttachment(attachment);
  const { status, url, retry } = useAuthenticatedAttachmentUrl(attachment);

  useEffect(() => {
    if (!open) return undefined;
    setIndex(Math.min(Math.max(initialIndex, 0), Math.max(total - 1, 0)));
  }, [initialIndex, open, total]);

  useEffect(() => {
    setZoom(1);
    setRotation(0);
    setPan({ x: 0, y: 0 });
  }, [index]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') setIndex((current) => Math.max(0, current - 1));
      if (event.key === 'ArrowRight') setIndex((current) => Math.min(total - 1, current + 1));
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, open, total]);

  if (!open || !attachment) return null;

  const fileName = getAttachmentName(attachment);
  const changeZoom = (nextZoom) => {
    const boundedZoom = Math.min(3, Math.max(0.5, nextZoom));
    setZoom(boundedZoom);
    if (boundedZoom <= 1) setPan({ x: 0, y: 0 });
  };

  const handleWheel = (event) => {
    if (isPdf) return;
    event.preventDefault();
    changeZoom(zoom + (event.deltaY < 0 ? 0.2 : -0.2));
  };

  const handlePointerDown = (event) => {
    if (isPdf || zoom <= 1) return;
    dragRef.current = { x: event.clientX, y: event.clientY, panX: pan.x, panY: pan.y };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!dragRef.current) return;
    setPan({
      x: dragRef.current.panX + event.clientX - dragRef.current.x,
      y: dragRef.current.panY + event.clientY - dragRef.current.y,
    });
  };

  const stopDragging = (event) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  return (
    <div className='fixed inset-0 z-[1000] flex flex-col overflow-hidden overscroll-none bg-slate-950/95 text-white' role='dialog' aria-modal='true' aria-label={t('Attachment Preview')}>
      <header className='flex min-h-16 items-center justify-between gap-3 border-b border-white/10 px-3 py-2 sm:px-5'>
        <div className='min-w-0'>
          <p className='truncate text-sm font-semibold'>{fileName}</p>
          <p className='mt-0.5 text-xs text-slate-400'>{index + 1} {t('of')} {total}</p>
        </div>
        <div className='flex items-center gap-2'>
          {!isPdf && (
            <>
              <span className='min-w-12 text-center text-xs font-semibold text-slate-300'>{Math.round(zoom * 100)}%</span>
              <ViewerButton label={t('Zoom out')} onClick={() => changeZoom(zoom - 0.25)} disabled={zoom <= 0.5}><FiZoomOut /></ViewerButton>
              <ViewerButton label={t('Zoom in')} onClick={() => changeZoom(zoom + 0.25)} disabled={zoom >= 3}><FiZoomIn /></ViewerButton>
              <ViewerButton label={t('Rotate')} onClick={() => setRotation((value) => value + 90)}><FiRotateCw /></ViewerButton>
            </>
          )}
          {status === 'ready' ? (
            <AppTooltip label={t('Download')}><a href={url} download={fileName} target='_blank' rel='noreferrer' className='flex h-10 w-10 items-center justify-center rounded-[var(--radius-md)] border border-white/15 bg-slate-950/65 text-white shadow-lg hover:bg-slate-800' aria-label={t('Download')}><FiDownload /></a></AppTooltip>
          ) : (
            <ViewerButton label={t('Download')} disabled><FiDownload /></ViewerButton>
          )}
          <ViewerButton label={t('Close preview')} onClick={onClose}><FiX /></ViewerButton>
        </div>
      </header>

      <div
        className={`relative flex min-h-0 flex-1 items-center justify-center overflow-hidden p-3 sm:p-6 ${!isPdf && zoom > 1 ? 'cursor-grab active:cursor-grabbing' : ''}`}
        onWheel={handleWheel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={stopDragging}
        onPointerCancel={stopDragging}
        onDoubleClick={() => !isPdf && changeZoom(zoom === 1 ? 2 : 1)}
        style={{ touchAction: isPdf ? 'auto' : 'none' }}
      >
        {status === 'loading' ? (
          <div className='flex flex-col items-center gap-3 text-slate-300' role='status'>
            <FiLoader className='h-9 w-9 animate-spin' aria-hidden='true' />
            <span className='text-sm font-semibold'>{t('Loading...')}</span>
          </div>
        ) : status === 'error' ? (
          <div className='flex flex-col items-center gap-3 text-center text-slate-300' role='alert'>
            <p className='text-sm font-semibold'>{t('Unable to load attachment')}</p>
            <button type='button' onClick={retry} className='rounded-[var(--radius-md)] border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold hover:bg-white/15'>{t('Try again')}</button>
          </div>
        ) : isPdf ? (
          <div className='flex h-full w-full max-w-6xl flex-col overflow-hidden rounded-[var(--radius-md)] border border-white/10 bg-slate-900'>
            <iframe src={url} title={fileName} className='h-full min-h-[70vh] w-full bg-white' />
          </div>
        ) : (
          <img
            src={url}
            alt={fileName}
            draggable='false'
            className='max-h-full max-w-full select-none object-contain shadow-2xl transition-transform duration-150'
            style={{ transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${zoom}) rotate(${rotation}deg)` }}
          />
        )}

        {total > 1 && (
          <>
            <ViewerButton label={t('Previous attachment')} onClick={() => setIndex((current) => current - 1)} disabled={index === 0} className='fixed left-2 top-1/2 -translate-y-1/2 sm:left-5'><FiChevronLeft className='h-6 w-6' /></ViewerButton>
            <ViewerButton label={t('Next attachment')} onClick={() => setIndex((current) => current + 1)} disabled={index === total - 1} className='fixed right-2 top-1/2 -translate-y-1/2 sm:right-5'><FiChevronRight className='h-6 w-6' /></ViewerButton>
          </>
        )}
      </div>

      <footer className='border-t border-white/10 px-4 py-2 text-center text-xs text-slate-400'>
        {t('Use arrow keys to browse attachments')}
      </footer>
    </div>
  );
};
